export const RETENTION_PERIODS = ['7d', '30d', '90d', '1y', 'forever'] as const;
export type TRetentionPeriod = (typeof RETENTION_PERIODS)[number];

export type TRetentionSource = 'flow' | 'folder' | 'default';

export const HISTORY_FLOOR_DAYS = 90;

// Forever is a century in the day columns, so every `days * 86400` comparison and every
// `now() - days` cutoff keeps working without a special case (automation §9.5).
export const FOREVER_DAYS = 36_500;

const DAYS: Record<TRetentionPeriod, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
  forever: null,
};

const LABELS: Record<TRetentionPeriod, string> = {
  '7d': '7 d',
  '30d': '30 d',
  '90d': '90 d',
  '1y': '1 y',
  forever: 'Forever',
};

export const isRetentionPeriod = (value: unknown): value is TRetentionPeriod =>
  typeof value === 'string' && (RETENTION_PERIODS as readonly string[]).includes(value);

export const periodDays = (period: TRetentionPeriod): number | null => DAYS[period];

export const periodLabel = (period: TRetentionPeriod): string => LABELS[period];

export const contentDaysOf = (period: TRetentionPeriod): number => DAYS[period] ?? FOREVER_DAYS;

export const historyDaysOf = (period: TRetentionPeriod): number => {
  const days = DAYS[period];
  return days === null ? FOREVER_DAYS : Math.max(HISTORY_FLOOR_DAYS, days);
};

export const periodCovering = (days: number): TRetentionPeriod =>
  RETENTION_PERIODS.find((period) => (DAYS[period] ?? Infinity) >= days) ?? 'forever';

export const comparePeriods = (a: TRetentionPeriod, b: TRetentionPeriod): number =>
  RETENTION_PERIODS.indexOf(a) - RETENTION_PERIODS.indexOf(b);

export interface IRetentionFolder {
  id: string;
  parentId: string | null;
  period: TRetentionPeriod | null;
}

export interface IResolvedRetention {
  period: TRetentionPeriod;
  source: TRetentionSource;
  folderId: string | null;
}

export function resolveFolderRetention(
  folderId: string | null,
  folders: ReadonlyMap<string, IRetentionFolder>,
  organizationPeriod: TRetentionPeriod,
): IResolvedRetention {
  const seen = new Set<string>();
  let at = folderId;
  while (at !== null && !seen.has(at)) {
    seen.add(at);
    const folder = folders.get(at);
    if (!folder) break;
    if (folder.period) return { period: folder.period, source: 'folder', folderId: folder.id };
    at = folder.parentId;
  }
  return { period: organizationPeriod, source: 'default', folderId: null };
}

export function resolveFlowRetention(
  flow: { folderId: string | null; period: TRetentionPeriod | null },
  folders: ReadonlyMap<string, IRetentionFolder>,
  organizationPeriod: TRetentionPeriod,
): IResolvedRetention {
  if (flow.period) return { period: flow.period, source: 'flow', folderId: null };
  return resolveFolderRetention(flow.folderId, folders, organizationPeriod);
}
